/**
 * 16 MATCHA — Offerings
 * section-jhc-offerings.js
 *
 * - Client-side sorting
 * - Keeps customer on the same page
 * - Restores original Shopify collection order
 * - No page reload
 * - No dependencies
 * - Shopify Theme Editor compatible
 */

(() => {
  'use strict';

  const instances = new WeakMap();

  // ---------------------------------------------------------------------
  // SORTING
  // ---------------------------------------------------------------------

  function initSorting(root) {
    const select = root.querySelector(
      '[data-offerings-sort]'
    );

    const grid = root.querySelector(
      '[data-offerings-grid]'
    );

    if (!select || !grid) {
      return;
    }

    const products = Array.from(
      grid.querySelectorAll(
        '[data-offerings-product]'
      )
    );

    if (!products.length) {
      return;
    }

    function getTitle(card) {
      return (
        card.dataset.title || ''
      ).trim();
    }

    function getPrice(card) {
      return Number(
        card.dataset.price || 0
      );
    }

    function getDate(card) {
      return Number(
        card.dataset.date || 0
      );
    }

    function getOriginalOrder(card) {
      return Number(
        card.dataset.originalOrder || 0
      );
    }

    function sortProducts(sortBy) {
      const sorted = [...products];

      switch (sortBy) {
        case 'title-ascending':
          sorted.sort((a, b) =>
            getTitle(a).localeCompare(
              getTitle(b),
              undefined,
              {
                sensitivity: 'base',
              }
            )
          );
          break;

        case 'title-descending':
          sorted.sort((a, b) =>
            getTitle(b).localeCompare(
              getTitle(a),
              undefined,
              {
                sensitivity: 'base',
              }
            )
          );
          break;

        case 'price-ascending':
          sorted.sort(
            (a, b) =>
              getPrice(a) -
              getPrice(b)
          );
          break;

        case 'price-descending':
          sorted.sort(
            (a, b) =>
              getPrice(b) -
              getPrice(a)
          );
          break;

        case 'date-descending':
          sorted.sort(
            (a, b) =>
              getDate(b) -
              getDate(a)
          );
          break;

        case 'date-ascending':
          sorted.sort(
            (a, b) =>
              getDate(a) -
              getDate(b)
          );
          break;

        case 'manual':
        default:
          sorted.sort(
            (a, b) =>
              getOriginalOrder(a) -
              getOriginalOrder(b)
          );
          break;
      }

      const fragment =
        document.createDocumentFragment();

      sorted.forEach((product) => {
        fragment.appendChild(product);
      });

      grid.appendChild(fragment);
    }

    select.addEventListener(
      'change',
      () => {
        sortProducts(select.value);
      }
    );

    return {
      select,
      grid,
    };
  }

  // ---------------------------------------------------------------------
  // SECTION INIT
  // ---------------------------------------------------------------------

  function initSection(root) {
    if (
      !root ||
      instances.has(root)
    ) {
      return;
    }

    const sorting =
      initSorting(root);

    instances.set(root, {
      sorting,
    });
  }

  // ---------------------------------------------------------------------
  // SECTION DESTROY
  // ---------------------------------------------------------------------

  function destroySection(root) {
    if (!instances.has(root)) {
      return;
    }

    instances.delete(root);
  }

  // ---------------------------------------------------------------------
  // FIND SECTION ROOT
  // ---------------------------------------------------------------------

  function getSectionRoot(target) {
    if (
      target?.matches?.(
        '[data-jhc-offerings]'
      )
    ) {
      return target;
    }

    return target?.querySelector?.(
      '[data-jhc-offerings]'
    );
  }

  // ---------------------------------------------------------------------
  // INIT ALL
  // ---------------------------------------------------------------------

  function initAll() {
    document
      .querySelectorAll(
        '[data-jhc-offerings]'
      )
      .forEach(initSection);
  }

  // ---------------------------------------------------------------------
  // INITIAL LOAD
  // ---------------------------------------------------------------------

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      initAll,
      {
        once: true,
      }
    );
  } else {
    initAll();
  }

  // ---------------------------------------------------------------------
  // SHOPIFY THEME EDITOR
  // ---------------------------------------------------------------------

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const root =
        getSectionRoot(
          event.target
        );

      if (root) {
        initSection(root);
      }
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const root =
        getSectionRoot(
          event.target
        );

      if (root) {
        destroySection(root);
      }
    }
  );
})();